import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { partnerSettingsKeys } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { getPartnerRangeParams } from "@/components/pages/guesteria-partners/utils/partnerAnalyticsPeriod";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";
import { useHotel } from "@/hooks/useHotel";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type {
  PartnerAttributionSource,
  PartnerOrdersCatalogFilter,
} from "@/types/partner-order";

// Task 0038 (owner, 2026-09-30): `range` comes straight from the shared
// `usePartnerRevenuePeriod` — no more named period, no "extend to next
// month" hack (the shared selector is future-inclusive at the source).
export const usePartnerOrders = (
  range: PartnerRevenueDateRange,
  catalog: PartnerOrdersCatalogFilter,
  page: number,
  limit: number,
  enabled = true,
  // Task 0041 — room-attribution filter, both optional.
  attribution?: PartnerAttributionSource,
  roomId?: string,
) => {
  const { hotelId } = useHotel();
  const periodParams = useMemo(() => getPartnerRangeParams(range), [range]);

  return useQuery({
    queryKey: [
      ...partnerSettingsKeys.settings(hotelId || ""),
      "orders",
      periodParams.from,
      periodParams.to,
      catalog,
      page,
      limit,
      attribution ?? null,
      roomId ?? null,
    ],
    enabled: Boolean(hotelId) && enabled,
    queryFn: () =>
      partnerSettingsService.getPartnerOrders(hotelId!, {
        from: periodParams.from,
        to: periodParams.to,
        catalog,
        page,
        limit,
        attribution,
        roomId,
      }),
    staleTime: 60 * 1000,
  });
};
